import React from "react";
import classNames from "classnames";
import Link from "next/link";

import { Action, Category } from "constants/events";
import { PromotedStrain } from "custom-types/Directus";
import { useEventTracker } from "hooks/useEventTracker";
import useFilters from "hooks/useFilters";
import { trackEvent } from "utils/trackEvent";

import Carousel from "components/botanic/Carousel";

import { DeviceScreenSizes } from "../../custom-types/DeviceScreenSizes";

type PropsType = {
  promotedStrains: PromotedStrain[];
  title?: string;
  asLink?: boolean;
  linkToDeals?: boolean;
  className?: string;
};

const StrainCarousel = ({
  promotedStrains,
  title,
  asLink,
  linkToDeals,
  className,
}: PropsType) => {
  const { selectFilterValue, addFilter } = useFilters();
  const selectedStrain = selectFilterValue("strain_name");
  const { publishEvent } = useEventTracker();

  const handleStrainClick = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    const strain = e.currentTarget?.dataset?.strain;
    addFilter({
      strain_name: strain,
    });
    publishEvent({
      action: Action.click,
      category: Category.dealsOnTopStrains,
      label: `strain hub quick access - ${strain}`,
    });
  };

  return (
    <>
      <Carousel
        title={
          title ? <h2 className="heading--m font-bold">{title}</h2> : undefined
        }
        cardsPerSlide={5}
        mobileCardWidth={120}
        className={classNames(className)}
      >
        {promotedStrains.map((strain) => {
          const strainIsSelected = strain.strain_name === selectedStrain;

          return asLink ? (
            <Link
              key={strain.strain_name}
              href={`/search/shop/promoted-strains?strain_name=${encodeURIComponent(
                strain.strain_name as string,
              )}${linkToDeals ? "&deal_active=true" : ""}`}
              onClick={() =>
                trackEvent(
                  Category.dealMenuStrains,
                  Action.click,
                  `strain name - ${strain.strain_name}`,
                )
              }
              className="flex flex-col items-center pt-xxl"
            >
              <div
                className="flex items-center justify-center mb-xxl text-white strain-diamond"
                style={{ backgroundColor: strain.color }}
              >
                <span className="strain-diamond__label">{strain.label}</span>
              </div>

              <div
                className={classNames("font-bold text-center", {
                  "text-green": !strainIsSelected,
                })}
              >
                {strain.strain_name}
              </div>
            </Link>
          ) : (
            <div
              role="button"
              tabIndex={0}
              key={strain.strain_name}
              onClick={handleStrainClick}
              onKeyPress={handleStrainClick}
              data-strain={strain.strain_name}
              className="flex flex-col items-center pt-xxl"
            >
              <div
                className="flex items-center justify-center mb-xxl text-white strain-diamond"
                style={{ backgroundColor: strain.color }}
              >
                <span className="strain-diamond__label">{strain.label}</span>
              </div>

              <div
                className={classNames("font-bold text-center", {
                  "text-green": !strainIsSelected,
                })}
              >
                {strain.strain_name}
              </div>
            </div>
          );
        })}
      </Carousel>
      <style jsx>{`
        .strain-diamond {
          transform: rotate(-45deg);
          width: 45px;
          height: 45px;
        }
        .strain-diamond__label {
          transform: rotate(45deg);
          font-size: 1em;
        }

        @media screen and (max-width: ${DeviceScreenSizes.TabletMax}px) and {
          .strain-diamond {
            width: 85px;
            height: 85px;
          }

          .strain-diamond__label {
            font-size: 2em;
          }
        }
        @media screen and (min-width: ${DeviceScreenSizes.Desktop}px) {
          .strain-diamond {
            width: 108px;
            height: 108px;
          }

          .strain-diamond__label {
            font-size: 3em;
            font-weight: 500;
          }
        }
      `}</style>
    </>
  );
};

export default StrainCarousel;
